import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Card, CardContent, Typography, Chip, Alert, Box, Button, Divider, Stack,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import QuizLayout from './QuizLayout';

export default function QuizResult() {
  const navigate = useNavigate();
  const location = useLocation();
  const result = location.state && location.state.result;

  if (!result) {
    return (
      <QuizLayout title="Result">
        <Alert severity="info" sx={{ mb: 2 }}>This result is no longer available. Take the quiz again to see fresh results.</Alert>
        <Button variant="contained" onClick={() => navigate('/quizzes')}>Back to quizzes</Button>
      </QuizLayout>
    );
  }

  const showAnyScore = result.show_score || result.show_pass_fail;

  return (
    <QuizLayout title="Your Results">
      {showAnyScore && (
        <Card sx={{ mb: 3 }}><CardContent>
          <Stack direction="row" spacing={2} alignItems="center">
            {result.show_score && (
              <Typography variant="h3" sx={{ fontWeight: 700 }}>{result.percent}%</Typography>
            )}
            {result.show_score && (
              <Typography color="text.secondary">{result.score} / {result.max_score} points</Typography>
            )}
            <Box sx={{ flexGrow: 1 }} />
            {result.show_pass_fail && (
              <Chip
                color={result.passed ? 'success' : 'error'}
                icon={result.passed ? <CheckCircleIcon /> : <CancelIcon />}
                label={result.passed ? 'Passed' : 'Not passed'}
              />
            )}
          </Stack>
        </CardContent></Card>
      )}

      {!showAnyScore && result.review_mode === 'none' && (
        <Alert severity="success" sx={{ mb: 2 }}>Quiz complete. Thanks for completing it!</Alert>
      )}

      {result.questions.map((q, i) => (
        <Card key={i} sx={{ mb: 2, borderLeft: 4, borderColor: q.is_correct ? 'success.main' : 'error.main' }}>
          <CardContent>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              {q.is_correct ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />}
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{q.prompt}</Typography>
            </Stack>
            {q.options.map((o) => {
              const chosen = q.selected_option_ids.includes(o.id);
              const correct = o.is_correct;
              return (
                <Typography key={o.id} variant="body2"
                  sx={{
                    color: correct ? 'success.main' : (chosen ? 'error.main' : 'text.secondary'),
                    fontWeight: (chosen || correct) ? 600 : 400,
                  }}>
                  {correct ? '✓ ' : (chosen ? '✗ ' : '  ')}{o.text}
                  {chosen && !correct ? ' (your answer)' : ''}
                </Typography>
              );
            })}
            {q.explanation && (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2" color="text.secondary">{q.explanation}</Typography>
              </>
            )}
          </CardContent>
        </Card>
      ))}

      <Button variant="contained" onClick={() => navigate('/quizzes')} sx={{ mt: 1 }}>Back to quizzes</Button>
    </QuizLayout>
  );
}
